// @avantia common-code
import { basicSorter, ensureDefined, HpQueryValue, isTrue, SMap } from '@avantia/client-and-server-utilities';
import { isDefined } from '@avantia/form-validation';
import { lodash } from '@avantia/lodash';
import { hasOwnProperty, HpEither } from '@avantia/logging-and-errors';
import { HpAnswer, HpAnswerValueTypes, HpQuestionDataTypes } from '@avantia/questionset-model';
import { BaseSyntheticEvent } from 'react';
import { registerConfigurationAction } from '../config/registerClientConfig';
import {
  HpClientQuestion,
  HpClientQuestionData,
  HpClientQuestionLookupItem,
  HpClientQuestionLookupItemMap,
  HpClientStandardInputTextAndStyle,
  HpControlValueTypes,
  HpEventTypes,
  HpStringAnswerSource
} from '../types';
import { HpMetaData } from './clientAndServerUtilities';
import { getCookie } from './clientCookies';

export interface HpGetAnswerValueResponse {
  answerSource: HpStringAnswerSource;
  answerValue: HpAnswerValueTypes;
}

export interface HpAnswerValueQuestionType {
  dataType: HpQuestionDataTypes;
  isDefaultImplicit?: boolean;
}

let experimentsKey: string;
registerConfigurationAction((config) => {
  ({ experimentsKey } = config);
});

export function getAnswerValue(
  answer: HpAnswer,
  question: HpAnswerValueQuestionType,
  usedBy: 'control' | 'prerequisites' | 'validation' | 'request' | 'reducer'
): HpGetAnswerValueResponse {
  const { dataType: answerType, isDefaultImplicit } = ensureDefined(question, 'question');
  if (!answer) {
    return { answerSource: 'unanswered', answerValue: null };
  }

  const { aggregator, customer, default: defaultAnswer } = answer;
  let res: HpGetAnswerValueResponse | undefined;

  if (isAnswered(customer)) {
    res = { answerValue: customer, answerSource: 'customer' };
  } else if (isAnswered(aggregator)) {
    res = { answerValue: aggregator as HpAnswerValueTypes, answerSource: 'aggregator' };
  } else if (isAnswered(defaultAnswer)) {
    if (usedBy !== 'control' || !isDefaultImplicit) {
      res = { answerValue: defaultAnswer, answerSource: 'default' };
    }
  }

  if (!res) {
    res = { answerSource: 'unanswered', answerValue: null };
  }

  if (res !== undefined) {
    if (answerType === 'string' && res.answerValue === null) {
      res.answerValue = '';
    } else if (answerType === 'array' && !lodash.isArray(res.answerValue)) {
      res.answerValue = [];
    }
  }

  return res;
}

export function getTextFromLookupMap(
  key: string,
  lookupMap: HpClientQuestionLookupItemMap,
  textProperty?: string | undefined
): string {
  const option: HpClientQuestionLookupItem | null | undefined = lookupMap[key];
  const optionText =
    option === null || option === undefined
      ? key
      : (textProperty ? option[textProperty] : undefined) || option.text || key;
  return optionText;
}

export function answersAreSame(type: HpQuestionDataTypes, a: HpAnswerValueTypes, b: HpAnswerValueTypes): boolean {
  if (!isDefined(a) || !isDefined(b)) {
    return a === b;
  }

  if (type === 'array') {
    const listA = a as string[];
    const listB = b as string[];
    if (listA.length === listB.length) {
      const len = listA.length;
      for (let i = 0; i < len; i++) {
        if (listA[i] !== listB[i]) {
          return false;
        }
      }

      return true;
    } else {
      return false;
    }
  }

  return a === b;
}

function isAnswered(answer: HpAnswerValueTypes | undefined): boolean {
  return answer !== null && answer !== undefined;
}

export interface HpGetControlValueResponse {
  answerSource: HpStringAnswerSource;
  answerValue: HpControlValueTypes;
}

export function getQuestionStandardTextAndStyle(question: HpClientQuestion): HpClientStandardInputTextAndStyle {
  return (question.data as HpClientQuestionData).textAndStyle.standard;
}

export function getControlValue(answer: HpAnswer, question: HpClientQuestion): HpGetControlValueResponse {
  const { answerValue, answerSource } = getAnswerValue(answer, question, 'control');
  return { answerSource, answerValue: toControlValue(answerValue) };
}

// This should always return a string, or an array of strings.
// If it returns null or undefined it will result in React erroring because
// the control went from uncontrolled to controlled state.
export function toControlValue(answer: HpAnswerValueTypes): HpControlValueTypes {
  switch (answer) {
    case undefined:
    case null:
      return '';
    case false:
      return 'false';
    case true:
      return 'true';
  }

  if (lodash.isArray(answer)) {
    return answer.map(String);
  }

  return typeof answer === 'string' ? answer : String(answer);
}

export function getQueryStringValue(name: string, url?: string): string | undefined {
  const usp = new URLSearchParams((url || document.location.href).split('?').slice(1).join('?'));
  const value = usp.get(name);
  return value === null ? undefined : value;
}

export type HpKeyToFindInput<ModelT> = keyof ModelT | { key: keyof ModelT; required: boolean };

export interface HpMappedKeyResult<ItemT> {
  key: string;
  value: ItemT;
}

export type HpGetKeyFunction<ModelT> = (key: keyof ModelT) => string;

export interface HpGetMappingKey<ModelT> {
  getKey: HpGetKeyFunction<ModelT>;
}

export function getKeyMapping<ModelT, ItemT>(
  keysToFind: HpKeyToFindInput<ModelT>[],
  items: SMap<ItemT>
): SMap<HpMappedKeyResult<ModelT>> & HpGetMappingKey<ModelT> {
  const map = findMatchingKeys(keysToFind, items);

  const getKey: HpGetKeyFunction<ModelT> = (key) => {
    const keyStr = String(key);
    const tmp: HpMappedKeyResult<ItemT> = map[keyStr];
    if (tmp) {
      return tmp.key;
    }

    throw new Error(`There is no key "${keyStr}" in the map.`);
  };

  const res = map as unknown as SMap<HpMappedKeyResult<ModelT>> & HpGetMappingKey<ModelT>;
  res.getKey = getKey;
  return res;
}

export function findMatchingKeys<ModelT, ItemT>(
  keysToFind: HpKeyToFindInput<ModelT>[],
  items: SMap<ItemT>
): SMap<HpMappedKeyResult<ItemT>> {
  let itemKeys = lodash.keys(items);
  const map: SMap<HpMappedKeyResult<ItemT>> = {};
  for (const keyToFind of keysToFind) {
    let lookupKey = keyToFind as string;
    let isRequired = true;
    if (typeof keyToFind !== 'string') {
      const { key: k, required } = keyToFind as { key: keyof ModelT; required: boolean };
      lookupKey = k as string;
      isRequired = required;
    }

    if (hasOwnProperty(items, lookupKey)) {
      map[lookupKey] = { key: lookupKey, value: items[lookupKey] };
    } else {
      const upperKey = lookupKey.substring(0, 1).toUpperCase() + lookupKey.substring(1);
      const foundKey = itemKeys.filter((k) => k.endsWith(upperKey))[0];
      if (foundKey) {
        map[lookupKey] = { key: foundKey, value: items[foundKey] };
      } else if (isRequired) {
        itemKeys = itemKeys.sort();
        throw new Error(
          `Unable to find a matching key for "${lookupKey}" in provided keys, [${
            itemKeys.length > 0 ? `"${itemKeys.join('", "')}"` : ''
          }].`
        );
      }
    }
  }

  return map;
}

/**
 * Determine if it's an Agent.
 * @param agentIdOrAnswers either an AgentID or Answers map.
 * @returns true if it's an Agent.
 */
export function isAgent(agentIdOrAnswers: HpEither<string, HpMetaData | undefined>): boolean {
  if (!agentIdOrAnswers) {
    return false;
  }

  if (typeof agentIdOrAnswers === 'string') {
    return !!agentIdOrAnswers;
  }

  const { agentId } = agentIdOrAnswers;
  return !!agentId;
}

export function setExperimentsInQuery(query: SMap<HpQueryValue>): boolean {
  const includeExperiments = isTrue(getCookie(experimentsKey));
  if (includeExperiments) {
    query.experiments = true;
  }

  return includeExperiments;
}

export function createVoidEvent(): BaseSyntheticEvent {
  const type: HpEventTypes = 'none';
  return { type, timeStamp: new Date().getTime() } as BaseSyntheticEvent;
}

export interface HpGetEventDetailResult {
  eventType: string;
  target: HTMLInputElement;
  targetId: string;
}

export function getEventDetail(event: BaseSyntheticEvent): HpGetEventDetailResult {
  const { currentTarget, target: eventTarget, type: eventType } = event;
  const target = (currentTarget || eventTarget || {}) as HTMLInputElement;
  return { target, targetId: target.id, eventType };
}

// TODO - If there is a conflict between two leaves, it will take the first one that it finds.
export function sortByMultipleFields<ModelT extends Record<string, any>>(
  fieldsToSortBy: string[],
  mapToSort: ModelT
): ModelT {
  if (!fieldsToSortBy.length) {
    return mapToSort;
  }

  const mapKeys = Object.keys(mapToSort);
  mapKeys.sort((a: string, b: string) => {
    let result = 0;
    for (const field of fieldsToSortBy) {
      const questionA = mapToSort[a];
      const questionB = mapToSort[b];
      let fieldA = findDeep(questionA, field);
      let fieldB = findDeep(questionB, field);
      if (typeof fieldA === 'string') {
        fieldA = fieldA.toLowerCase();
      }

      if (typeof fieldB === 'string') {
        fieldB = fieldB.toLowerCase();
      }

      if (fieldA && fieldB) {
        result = basicSorter(fieldA, fieldB);
      } else if (!fieldA && fieldB) {
        result = 1;
      } else if (fieldA && !fieldB) {
        result = -1;
      }

      if (result) {
        break;
      }

      continue; // If fields are the same, order using next field in fieldsToSortBy.
    }

    return result;
  });

  const sortedMap: SMap<any> = {}; // TODO - This will not work with numeric keys, use Map or array instead.
  for (const key of mapKeys) {
    sortedMap[key] = mapToSort[key];
  }

  return sortedMap as ModelT;
}

function findDeep(node: any, field: string): string | number | undefined {
  if (node === field) {
    return node[field];
  }

  let result: string | number | undefined;
  for (const leaf in node) {
    if (node[leaf] && typeof node[leaf] === 'object') {
      result = findDeep(node[leaf], field);
      if (result) {
        return result;
      }
    } else if (leaf === field) {
      return node[leaf];
    }
  }

  return result;
}

interface ProcessGroupElementProps<ElementT extends HpElementToProcess> {
  items: ElementT[];
  reprocessAlignedItems: boolean;
  getTop: (element: ElementT) => number | null;
  action: (element: ElementT, isBottomRow: boolean) => boolean;
}

export interface HpElementToProcess {
  hpLayoutFix?: boolean;
}

/**
 * Used to process an array of elements that might wrap over multiple lines.
 * If elements are in multiple lines, the passed in action will be called for each them.
 * It is up to the caller to apply any changes to the items.
 * The function caches the already processed items so they don't get reprocessed.
 * @param reprocessAlignedItems - If true, the caching will be ignored and items will be reprocessed.
 */
export function processGroupElement<ElementT extends HpElementToProcess>({
  items,
  reprocessAlignedItems,
  getTop,
  action
}: ProcessGroupElementProps<ElementT>): void {
  if (items.length === 0) {
    return;
  }

  const lastItem = items[items.length - 1];
  // If one of the items has already been processed, that means that there are no new changes to the items.
  if (!reprocessAlignedItems && lastItem.hpLayoutFix) {
    return;
  }

  const firstItem = items[0];
  const firstItemTop = getTop(firstItem);
  const lastItemTop = getTop(lastItem);
  if (!firstItemTop || !lastItemTop || (lastItemTop === firstItemTop && !lastItem.hpLayoutFix)) {
    // Exit early if there is only one line of items and last item has not been processed.
    return;
  }

  for (let i = items.length - 1; i >= 0; i--) {
    const currentItem = items[i];
    const currentLineTop = getTop(currentItem);
    if (action(currentItem, currentLineTop === lastItemTop) === false) {
      if (currentItem.hpLayoutFix) {
        delete currentItem.hpLayoutFix;
        continue;
      }

      break;
    }

    currentItem.hpLayoutFix = true;
  }
}

/**
 * Converts a string from skewer-case to camelCase.
 *
 * For example, 'hello-world' becomes 'helloWorld'.
 */
export function convertSkewerToCamelCase(text: string | undefined | null): string {
  if (!text) {
    return '';
  }

  return String(text).replace(/-./g, (x) => x[1].toUpperCase());
}

/**
 * Converts a string from camelCase to skewer-case.
 *
 * For example, 'helloWorld' becomes 'hello-world'.
 */
export function convertCamelToSkewerCase(text: string | undefined | null): string {
  if (!text) {
    return '';
  }

  return String(text).replace(/[A-Z]/g, (x) => `-${x.toLowerCase()}`);
}
